import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
} from '@angular/core';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';

import {
  CurrentPatientScope,
  OrganisationService,
  StafferSettingsStoreService,
} from '@principle-theorem/ng-principle-shared';
import { BreadcrumbsComponent } from '@principle-theorem/ng-shared';
import { stafferToNamedDoc } from '@principle-theorem/principle-core';

import {
  IReferral,
  MentionResourceType,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  filterUndefined,
  getError,
  snapshot,
  toNamedTypeDocument,
} from '@principle-theorem/shared';
import { map, skip, take } from 'rxjs/operators';
import { ReferralListFiltersComponent } from '../../components/referral-list-filters/referral-list-filters.component';
import { ReferralListComponent } from '../../components/referral-list/referral-list.component';
import { ReferralService } from '../../services/referral.service';
import { ReferralListStore } from '../../stores/referral-list.store';

@Component({
  selector: 'pr-brand-referral-dashboard',
  imports: [
    NgCommonModule,
    BreadcrumbsComponent,
    ReferralListComponent,
    ReferralListFiltersComponent,
    MatButtonModule,
    MatIconModule,
    RouterModule,
  ],
  templateUrl: './referral-dashboard.component.html',
  styleUrl: './referral-dashboard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralDashboardComponent {
  private _destroyRef = inject(DestroyRef);
  private _organisation = inject(OrganisationService);
  private _patientScope = inject(CurrentPatientScope);
  private _stafferSettings = inject(StafferSettingsStoreService);
  public referralService = inject(ReferralService);
  public store = inject(ReferralListStore);
  patient$ = this._patientScope.doc$;
  breadcrumbs = toSignal(this.referralService.getBreadcrumbs$(this.patient$));

  constructor() {
    this.store.setLoading(true);
    const brand$ = this._organisation.brand$.pipe(filterUndefined());
    const patient$ = this._patientScope.doc$;
    const referrals$ = this.referralService.getReferrals$(brand$, patient$);

    this._subscribeToCurrentStaffer();
    this._restoreFiltersFromSettings();
    this._saveFiltersToSettings();

    this.store.loadReferrals(referrals$);
    this.store.setLoading(false);
  }

  async createReferral(): Promise<void> {
    await this.referralService.openCreateDialog();
  }

  async deleteReferral(referral: WithRef<IReferral>): Promise<void> {
    try {
      await this.referralService.delete(referral);
      this.store.removeReferral(referral);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(getError(error));
    }
  }

  async updateReferral(
    referral: WithRef<IReferral>,
    changes: Partial<WithRef<IReferral>>
  ): Promise<void> {
    await this.referralService.update(referral, changes);
  }

  async navigateToReferral(referral: WithRef<IReferral>): Promise<void> {
    const patient = await snapshot(this.patient$);
    await this.referralService.navigateToReferral(referral, patient);
  }

  private _subscribeToCurrentStaffer(): void {
    this._organisation.staffer$
      .pipe(
        filterUndefined(),
        map((staffer) =>
          toNamedTypeDocument(
            stafferToNamedDoc(staffer),
            MentionResourceType.Staffer
          )
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((staffer) => this.store.setCurrentStaffer(staffer));
  }

  private _restoreFiltersFromSettings(): void {
    this._stafferSettings.referrals$
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe((savedSettings) => {
        const savedFilters = savedSettings.filters;
        this.store.updateFilters({
          status: savedFilters.status ?? [],
          type: savedFilters.type ?? [],
          needsActionOnly: savedFilters.needsActionOnly ?? false,
        });
      });
  }

  private _saveFiltersToSettings(): void {
    this.store.filters$
      .pipe(skip(1), takeUntilDestroyed(this._destroyRef))
      .subscribe((filters) => {
        this._stafferSettings.updateStafferSettings({
          referrals: {
            filters: {
              status: filters.status,
              type: filters.type,
              needsActionOnly: filters.needsActionOnly,
            },
          },
        });
      });
  }
}
